import { colorTokens } from '@kariusdx/design-system';
import { Box, Button, CircularProgress, Grid, Modal, TextField, Typography } from '@mui/material';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { EngagementEvents, logKariusEvent } from '../Analytics';
import Colors from '../theme/colors';

interface PasswordResetModalProps {
  open: boolean;
  isResetEmailExpired: boolean;
  resetEmail: string | null;
  handleClose: () => void;
}

const style = {
  position: 'absolute',
  top: '35%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 410,
  height: 266,
  backgroundColor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

export const PasswordResetModal: React.FC<PropsWithChildren<PasswordResetModalProps>> = ({
  open,
  isResetEmailExpired,
  resetEmail,
  handleClose,
}) => {
  const [textInput, setTextInput] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasResetEmail, setHasResetEmail] = useState(false);

  if (isResetEmailExpired && resetEmail && !hasResetEmail) {
    setTextInput(resetEmail);
    setHasResetEmail(true);
  }

  const modelTitle = useMemo(() => {
    if (isResetEmailExpired) {
      return `Password link expired`;
    }
    return emailSent ? 'Reset password email sent' : 'Reset password';
  }, [isResetEmailExpired, emailSent]);

  const handleSubmit = async () => {
    logKariusEvent(EngagementEvents.reset_password_submit_tapped, {
      modal_title: modelTitle,
      email_or_username: textInput,
    });
    setErrorMessage('');
    setLoading(true);
    const networkErrorMessage =
      'Failed to reset password. Please contact Customer Success at (866) 452-7487 or help@kariusdx.com';
    try {
      const res = await fetch('/api/auth0/passwordReset', {
        method: 'POST',
        body: JSON.stringify({
          text: textInput,
          modelTitle: modelTitle,
        }),
      });
      if (res.ok) {
        setEmailSent(true);
      } else {
        setErrorMessage(networkErrorMessage);
      }
    } catch (e) {
      setErrorMessage(networkErrorMessage);
    } finally {
      setLoading(false);
    }
  };
  const closeModal = () => {
    if (!emailSent) {
      logKariusEvent(EngagementEvents.reset_password_cancel_tapped, {
        modal_title: modelTitle,
      });
    }
    setTextInput('');
    setEmailSent(false);
    setErrorMessage('');
    handleClose();
  };
  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Typography
          id='modal-modal-title'
          variant='h2'
          sx={{
            fontSize: '20px !important',
            fontWeight: '400',
            lineHeight: '28px',
            letterSpacing: '0.15px',
          }}
        >
          {modelTitle}
        </Typography>
        <Grid
          id='modal-modal-description'
          container
          sx={{
            '& .link-text': {
              color: Colors.teal,
              fontWeight: 500,
            },
          }}
        >
          <Box
            sx={{
              height: '155px',
            }}
          >
            {!emailSent && (
              <>
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    marginTop: '20px',
                    letterSpacing: '0.25px',
                  }}
                >
                  Enter the email address or username for your account and an email will be sent that enables you to
                  create a new password
                </Typography>
                <TextField
                  variant='outlined'
                  id='email'
                  name='email'
                  label='Email or username*'
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    marginTop: '12px',
                    fontSize: '14px',
                    fontWeight: '400px',
                    width: '100%',
                    '& input': {
                      height: '12px',
                    },
                  }}
                />
              </>
            )}

            {emailSent && (
              <Typography
                variant='body2'
                sx={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  marginTop: '20px',
                  letterSpacing: '0.25px',
                }}
              >
                If you don’t see the email in your inbox, please check your spam folder.
                <br />
                <br />
                <>
                  If you need assistance, please contact Karius at <br />
                  <a href={'tel:8664527487'} className='link-text'>
                    (866) 452-7487
                  </a>{' '}
                  or{' '}
                  <a href={'mailto:help@kariusdx.com'} className='link-text'>
                    help@kariusdx.com
                  </a>
                  .
                </>
              </Typography>
            )}

            {errorMessage && (
              <Typography
                variant='body2'
                sx={{
                  lineHeight: '20px',
                  marginLeft: '10px',
                  letterSpacing: '0.25px',
                  color: colorTokens.text_error,
                }}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>
          <Grid
            item
            xs={12}
            mt='20px'
            sx={{
              textAlign: 'right',
              gap: '12px',
            }}
          >
            <Button
              color='primary'
              variant={emailSent ? `outlined` : `text`}
              sx={{
                borderRadius: '20px',
                fontSize: '12px',
                textTransform: 'unset',
                width: emailSent ? '110px' : 'auto',
              }}
              onClick={closeModal}
            >
              {emailSent ? 'Close' : 'Cancel'}
            </Button>
            {!emailSent && (
              <Button
                color='primary'
                variant='contained'
                type='button'
                disabled={!textInput || loading}
                sx={{
                  borderRadius: '20px',
                  fontSize: '12px',
                  marginLeft: '5px',
                  textTransform: 'unset',
                }}
                onClick={handleSubmit}
              >
                {loading && <CircularProgress size={13} sx={{ color: Colors.black, marginRight: '5px' }} />}Submit
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
